import * as React from "react";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { MobXProviderContext, observer } from "mobx-react";
import { RouteStore } from "bernie-core";
import { Navigation, ExternalLinkTabConfig } from "src/stores/wizard/config";
import { FlexComponentNameToWizardStateConfig } from "../FlexComponentNameToWizardStateConfig";
import { useLocalization } from "@shared-ui/localization-context";
import { TabbedWizard } from "./TabbedWizard";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";

/**
 * @property lobForms: a list of React elements representing each LOB form added in the flex manager
 */
export interface LobFormWithTabsProps {
  lobForms: React.ReactElement[];
  wizardFormClassNames?: string;
  tabGroupClassNames?: string;
  trackOnChangeTab?: (tabName: string) => void;
  context?: ExtendedContextStore;
}

export const LobFormWithTabs: React.FunctionComponent<LobFormWithTabsProps> = observer(
  ({ lobForms, wizardFormClassNames, tabGroupClassNames, trackOnChangeTab, context }) => {
    const keyHelperWizard = new ItemKeyHelper("storefrontForms");
    const keyHelperNavigation = new ItemKeyHelper("storefrontWizardNavigation");
    const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
    const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
    const defaultLobNavConfig: Navigation = {
      tabIcon: "help",
      tabTitleToken: "",
    };
    const externalLinkTabWizardFlexModuleName = "wizard-externalLinkTab-pwa";
    const { formatText } = useLocalization();

    //returns the localized string given the name of the flex component used
    const getLabel = (lobName: string): string => {
      let label = defaultLobNavConfig.tabTitleToken;
      if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
        label = formatText(wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabTitleToken);
      }

      return label;
    };

    const getExternalLinkTabConfig = (lob: React.ReactElement): ExternalLinkTabConfig | null => {
      const { flexComponent } = lob?.props;
      const { name: lobName } = flexComponent;
      const isExternalLinkTabModule = lobName === externalLinkTabWizardFlexModuleName;

      if (!isExternalLinkTabModule) {
        return null;
      }

      const { tabURL, iconKey, labelToken, addLocaleToURL } = flexComponent?.model;
      const localConfig: ExternalLinkTabConfig = wizardState?.externalLinkTabWizardState?.config;
      const label = labelToken && formatText(labelToken);

      return {
        tabURL: tabURL || localConfig?.tabURL,
        addLocaleToURL: addLocaleToURL !== undefined ? addLocaleToURL : localConfig?.addLocaleToURL,
        navigation: {
          tabIcon: iconKey || wizardState[FlexComponentNameToWizardStateConfig[lobName]]?.config?.navigation?.tabIcon,
          tabTitleToken: label || getLabel(lobName),
        },
      };
    };

    const getExternalLinkTabURL = (lobName: string): string | null => {
      if (lobName !== externalLinkTabWizardFlexModuleName) {
        return null;
      }

      const externalLinkLobs = lobForms.filter(
        (lob) => lob?.props?.flexComponent?.name === externalLinkTabWizardFlexModuleName
      );
      const config: ExternalLinkTabConfig | null =
        externalLinkLobs?.length > 0 ? getExternalLinkTabConfig(externalLinkLobs[0]) : null;

      if (config?.addLocaleToURL) {
        return `${config?.tabURL}&locale=${context?.locale}`;
      } else {
        return config?.tabURL || null;
      }
    };

    //set the state to hold the default lob in the wizard store as the current value
    const [currentLOB, setCurrentLOB] = React.useState<string>(getLabel(wizardState.activeLob));

    /**
     * Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
     * CurrentLOB and defaultLOB set server side, but may be updated based on last search cookie value client side
     * To ensure up to date values on client side render, trigger an effect that does not have any dependent values to only run on first render
     */
    React.useEffect(() => {
      if (!routeStore.queryParams.pwaLob) {
        const { useDefaultLOB, defaultLob: pwaLob } = wizardState;
        if (useDefaultLOB) {
          wizardState.setUseDefaultLOB(false);
          setCurrentLOB(getLabel(pwaLob));
        }
      }
    }, []);

    //need to be able to map the localized version of the lob form name to the flex component name
    const LobFormNames: { [label: string]: string } = {};
    lobForms.map((lob: React.ReactElement) => {
      const formName = lob.props.flexComponent.name;
      const formLabel = getLabel(formName);
      LobFormNames[formLabel] = formName;
    });

    const getFormNameFromLabel = (label: string): string => {
      return LobFormNames[label];
    };

    //on selecting a tab, update the wizard state with the flex component name of the current tab, and update the react state with the localized label that will be used on the react component
    //update the query route with the new pwaLob and pwaSubLob
    const onTabSelect = (tabSelected: any) => {
      const formName = getFormNameFromLabel(tabSelected);
      if (trackOnChangeTab) {
        trackOnChangeTab(formName);
      }

      if (formName !== externalLinkTabWizardFlexModuleName) {
        wizardState.setActiveLob(formName);
        setCurrentLOB(tabSelected);
        resetErrorsState();
      } else {
        const groupsURL = getExternalLinkTabURL(formName);
        if (groupsURL) {
          window.open(groupsURL);
        }
      }
    };

    const resetErrorsState = () => {
      wizardState.carWizardState.resetSubLobState();
      wizardState.carWizardState.resetSubLobTabs();
      wizardState.carWizardState.cleanErrorState();

      wizardState.lxWizardState.cleanErrorState();
      wizardState.hotelWizardState.cleanErrorState();
      wizardState.flightWizardState.cleanErrorState();
      wizardState.packageWizardState.cleanErrorState();
    };

    const lobIconsMap = {
      "wizard-hotel-pwa-v2": "lob_hotels",
      "wizard-flight-pwa": "lob_flights",
      "wizard-package-pwa": "lob_packages",
      "wizard-car-pwa": "lob_cars",
      "wizard-lx-pwa": "lob_activities",
      "wizard-externalLinkTab-pwa": "people",
      "wizard-cruise-pwa": "lob_cruise",
      "wizard-vr-pwa": "lob_vacation_rentals",
    };

    return (
      <>
        <UitkExperimentalButtonTabs
          onTabSelect={onTabSelect}
          selectedTab={currentLOB}
          tabsType={UitkTabsType.NATURAL}
          className={`lobNavigationFormWithTabs ${tabGroupClassNames ? tabGroupClassNames : ""}`}
          data-testid="lobNavigationFormWithTabs"
          tabAnchorType={UitkTabAnchorType.ICON_AND_TEXT}
        >
          {lobForms.map((lob: React.ReactElement) => {
            const externalLinkConfig = getExternalLinkTabConfig(lob);
            const { name } = lob.props.flexComponent;

            const iconKey = externalLinkConfig?.navigation?.tabIcon || lobIconsMap[name];
            const displayLabel = externalLinkConfig?.navigation?.tabTitleToken || undefined;

            return (
              <UitkTab
                displayName={displayLabel}
                name={getLabel(name)}
                targetURI={getExternalLinkTabURL(name) || `?pwaLOB=${name}`}
                id={name}
                key={keyHelperNavigation.next()}
                iconKey={iconKey}
              >
                {currentLOB === getLabel(name) && (
                  <div className={wizardFormClassNames || ""}>
                    <TabbedWizard key={keyHelperWizard.next()}>{lob}</TabbedWizard>
                  </div>
                )}
              </UitkTab>
            );
          })}
        </UitkExperimentalButtonTabs>
      </>
    );
  }
);
